import { useMediaThumbnail } from '@lib/image/thumbnail';
import { formatDurationToString } from '@lib/utils/units';
import { getLibraryUrl } from '@lib/utils/url';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { observer } from 'mobx-react';
import { useCallback, useMemo } from 'react';
import { BatchCard, BatchCardViewModel } from './BatchCard';
import { BatchNotification, deriveBatchState } from './batchState';
import { resolveBillingErrorPriority } from './billingPriority';

interface BatchCardLiveProps {
  notification: BatchNotification;
  onDismiss: () => void;
  formatTime: (timestamp: number) => string;
  onOpenResults?: (notificationId: string) => void;
  onRetryFailed?: (notificationId: string) => void;
  onDeleteBatch?: (notificationId: string) => void;
  onCancelBatch?: (notificationId: string) => void;
}

/**
 * Live wrapper around the (dumb) `BatchCard`. Reads `notificationStore` /
 * `uploadManager` / `useMediaThumbnail` and reduces them into a single
 * `BatchCardViewModel` that the presentational component can render off of.
 *
 * The dumb `BatchCard` lives one level deeper and is the one the
 * `ToastDebugPage` mounts directly with mock VMs to simulate in-flight
 * states without a real upload.
 */
export const BatchCardLive = observer(function BatchCardLive({
  notification,
  onDismiss,
  formatTime,
  onOpenResults,
  onRetryFailed,
  onDeleteBatch,
  onCancelBatch,
}: BatchCardLiveProps) {
  const uploadManager = useUploadManager();
  const libraryManager = useLibraryManager();
  const state = deriveBatchState(notification, uploadManager);

  // Whose balance a billing failure on this batch is actually about. The backend charges the
  // target library's `billingId`, so on a library billed to another account a 402 means *that*
  // account ran out — and the default failure row's top-up CTA charges this user instead.
  //
  // Not keyed on who owns the library: on a shared org billing account a teammate's library is
  // billed to the reader, so a 402 there really is theirs to fix and withholding the top-up CTA
  // would leave them no recovery at all while telling them a top-up wouldn't help.
  //
  // `libraryById` can return undefined (a batch for a library this session never fetched), and
  // the store's `isBilledToAnotherAccount` is itself false while billing ownership is `unknown`;
  // both land on false, which keeps the pre-existing top-up row. That's the right way to fail: an
  // unnecessary top-up offer is recoverable, silently withholding the only recovery CTA isn't.
  const targetLibrary = libraryManager.libraryById(notification.libraryId);
  const billedToAnotherAccount = targetLibrary?.isBilledToAnotherAccount ?? false;
  // Who to name, from the ownership axis. Undefined whenever we can't name them — an unfetched
  // library, an owner the backend gave neither a name nor an email, or a library billed
  // elsewhere that the reader owns. The row then uses its nameless wording; it never goes silent.
  const ownerName = targetLibrary?.otherOwnerName;

  // CDN preview overlays — `useMediaThumbnail` short-circuits when libraryId
  // or mediaId are undefined, so we can call it unconditionally and let it
  // no-op for empty mediaIds (queued / uploading / fresh-processing batches).
  const primaryFallbackMediaId =
    notification.mediaIds.length > 0 ? notification.mediaIds[0] : undefined;
  const secondaryFallbackMediaId =
    notification.mediaIds.length > 1 ? notification.mediaIds[1] : undefined;
  const { url: primaryUrl, token: primaryToken } = useMediaThumbnail(
    primaryFallbackMediaId ? notification.libraryId : undefined,
    primaryFallbackMediaId,
  );
  const { url: secondaryUrl, token: secondaryToken } = useMediaThumbnail(
    secondaryFallbackMediaId ? notification.libraryId : undefined,
    secondaryFallbackMediaId,
  );

  const failures = uploadManager.getBatchFailures(notification);
  const failedCount = failures.uploadFailures.length + failures.processingFailures.length;
  const retryableCount = failures.pendingUploadFailures.length + failures.processingFailures.length;
  const hasCreditFails = failures.uploadFailures.some((r) => r.category === 'credits');
  const hasStorageFails = failures.uploadFailures.some((r) => r.category === 'storage');
  // Blame the connection only when it explains EVERY failure in the batch — a
  // mixed batch (one file out of credits, one dropped) would otherwise hide the
  // billing problem behind "unstable connection".
  const allConnectionFails =
    failedCount > 0 &&
    failures.processingFailures.length === 0 &&
    failures.uploadFailures.every((r) => r.category === 'connection');
  const { showStorageErrors, showCreditErrors } = resolveBillingErrorPriority(
    hasStorageFails,
    hasCreditFails,
  );

  const isQueued = state === 'queued';
  const isPreparing = state === 'preparing';
  const isUploading = state === 'uploading';
  const isStalled = state === 'stalled';
  const isWrappingUp = state === 'wrapping-up';
  const isProcessing = state === 'processing';

  // Progress bar value — same logic as the previous in-component derivation.
  const displayProgress = (() => {
    if (isWrappingUp) return 1;
    if (notification.type === 'upload-file') {
      // `isStalled` belongs with the transfer states: a stalled batch is still
      // mid-upload, so it keeps reporting uploadProgress. Falling through to
      // processingProgress would snap the bar back to ~0 the moment the
      // connection hiccuped, which reads as losing all the transferred bytes.
      return isQueued || isPreparing || isUploading || isStalled
        ? notification.uploadProgress
        : notification.processingProgress;
    }
    return notification.progress;
  })();

  // Time-line text. This needs uploadManager state for the uploading/queued
  // branches, so it has to live in the wrapper rather than the dumb card.
  const uploadUuids = notification.type === 'upload-file' ? notification.uploadUuids : [];
  // ETA reuses the smoothed GLOBAL speed (see UploadManager.getBatchUploadStats)
  // so this batch's countdown agrees with the panel banner, instead of running a
  // second, divergent per-file smoothing pass over the same bytes.
  const batchStats = isUploading ? uploadManager.getBatchUploadStats(uploadUuids) : null;
  const timeLineText: string | null = (() => {
    if (isUploading) {
      const eta = batchStats?.etaSeconds ?? -1;
      if (eta > 0 && isFinite(eta)) {
        return `About ${formatDurationToString(eta, { detail: 2 })} left`;
      }
      return 'Calculating ETA…';
    }
    if (isQueued) {
      return uploadManager.uploadingFiles.length > 0
        ? 'Waiting for other uploads to finish…'
        : 'Please wait…';
    }
    if (isPreparing) return 'Calculating ETA…';
    // No ETA is meaningful while nothing is moving, and "Calculating ETA…"
    // would imply progress is being made. Say what is actually happening.
    if (isStalled) return 'Trying to reconnect…';
    // Wrapping-up is the brief window where chunks finished transferring but
    // `finishFileUpload` is still being awaited server-side. By design the
    // user can't cancel or delete at this point (BatchCardMenu hides those
    // items), so the timeline just reassures them it's nearly there instead
    // of going blank.
    if (isWrappingUp) return 'Anytime now…';
    if (isProcessing) return 'Feel free to check back later…';
    // Terminal states render formatTime(timestamp); the dumb card handles
    // that itself given the timestamp.
    return null;
  })();

  // Memo deps are every primitive the VM depends on. The wrapper re-renders
  // every 500ms while uploads are active (mobxNow tick inside
  // getBatchUploadStats), so without this memo BatchCard would re-render
  // every tick even when no field actually changed. With it, BatchCard
  // re-renders only when the VM's identity flips — which happens on real
  // observable change.
  const succeededCount = failures.succeededCount;
  const hasBillingFails = hasCreditFails || hasStorageFails;
  const failedProgress =
    notification.type === 'upload-file' ? (notification.failedUploadProgress ?? 0) : 0;

  const vm: BatchCardViewModel = useMemo(
    () => ({
      id: notification.id,
      title: notification.title,
      libraryName: notification.libraryName,
      libraryUrl: getLibraryUrl(notification.libraryId, notification.libraryName),
      state,
      mediaCount: notification.mediaTitles.length,
      mediaTitles: notification.mediaTitles,
      isAudio: notification.isAudio,
      thumbnailUrl: notification.thumbnailUrl,
      secondaryThumbnailUrl: notification.secondaryThumbnailUrl,
      gradientSeed: notification.gradientSeed,
      secondaryGradientSeed: notification.secondaryGradientSeed,
      primaryCdnPreview:
        primaryUrl && primaryToken ? { url: primaryUrl, token: primaryToken } : null,
      secondaryCdnPreview:
        secondaryUrl && secondaryToken ? { url: secondaryUrl, token: secondaryToken } : null,
      displayProgress,
      failedProgress,
      timeLineText,
      failedCount,
      retryableCount,
      succeededCount,
      hasBillingFails,
      showCreditErrors,
      showStorageErrors,
      allConnectionFails,
      billedToAnotherAccount,
      ownerName,
      timestamp: notification.timestamp,
      skippedDuplicates:
        notification.type === 'upload-file' && notification.skippedDuplicates
          ? notification.skippedDuplicates
          : [],
    }),
    [
      notification.id,
      notification.title,
      notification.libraryId,
      notification.libraryName,
      notification.mediaTitles,
      notification.isAudio,
      notification.thumbnailUrl,
      notification.secondaryThumbnailUrl,
      notification.gradientSeed,
      notification.secondaryGradientSeed,
      notification.timestamp,
      notification.type === 'upload-file' ? notification.skippedDuplicates : undefined,
      state,
      primaryUrl,
      primaryToken,
      secondaryUrl,
      secondaryToken,
      displayProgress,
      failedProgress,
      timeLineText,
      failedCount,
      retryableCount,
      succeededCount,
      hasBillingFails,
      showCreditErrors,
      showStorageErrors,
      allConnectionFails,
      billedToAnotherAccount,
      ownerName,
    ],
  );

  // Stabilise the bound callbacks so React.memo on BatchCard can actually
  // bail on re-render when the VM is unchanged. Each callback closes over
  // notification.id (stable for the notification's lifetime) plus the raw
  // parent callback (stable as long as the parent holds it stable).
  const id = notification.id;
  const handleOpenResults = useCallback(() => onOpenResults?.(id), [onOpenResults, id]);
  const handleRetryFailed = useCallback(() => onRetryFailed?.(id), [onRetryFailed, id]);
  const handleDeleteBatch = useCallback(() => onDeleteBatch?.(id), [onDeleteBatch, id]);
  const handleCancelBatch = useCallback(() => onCancelBatch?.(id), [onCancelBatch, id]);

  return (
    <BatchCard
      vm={vm}
      formatTime={formatTime}
      onDismiss={onDismiss}
      onOpenResults={onOpenResults ? handleOpenResults : undefined}
      onRetryFailed={onRetryFailed ? handleRetryFailed : undefined}
      onDeleteBatch={onDeleteBatch ? handleDeleteBatch : undefined}
      onCancelBatch={onCancelBatch ? handleCancelBatch : undefined}
    />
  );
});
